import { supabase } from './supabase'
import { StudioRankingData, StudioRankings, DailyTotals } from '@/types'
import { listPartnerCoverage } from '@/services/territorialManagerService'
import { getLocalIsoDate } from '@/lib/analytics/periodCalculations'
import { resolveRangeEndMetricValue, resolveRangeGrowthStartMetricValue } from '@/lib/analytics/clientMetrics'
import { fetchStudioClientRangeMetrics } from '@/lib/analytics/studioClientRangeMetrics'
import { isRatingEligibleStudioStatus, loadEffectiveStudioStatusMap } from '@/lib/studioStatus'

const RANKING_TOP_COUNT = 10
const RANKING_NEIGHBOR_RADIUS = 5

type RankingRow = {
  studio_internal_id: number
  studio_number: string
  city_name?: string
  country?: string | null
  land_group_name?: string | null
  population_group_description?: string | null
  month_year?: string
  total_revenue: number
  global_rank?: number
  global_total?: number
  regional_rank?: number
  regional_total?: number
  size_group_rank?: number
  size_group_total?: number
  region_name?: string | null
  city_population_group?: number | null
  size_group_name?: string | null
}

type PartnerRankingRow = {
  partner_number: number
  persons_fio?: string | null
  cities_text?: string | null
  total_revenue: number
  partner_rank: number
  partner_total: number
}

type RangeStudioMetaRow = {
  studio_internal_id: number
  studio_number: string
  city_name?: string | null
  region_name?: string | null
  city_population_group?: number | null
  size_group_name?: string | null
}

type RevenueRangeRow = {
  studio_number: number | string
  partner_number: number | null
  metric_date: string
  gross_revenue: number | null
}

type OwnershipRow = {
  ownership_id: number
  studio_internal_id: number
  start_date: string | null
  end_date: string | null
  updated_at: string | null
  contract_series: { partner_number: number | null } | Array<{ partner_number: number | null }>
}

type ClientMetricSnapshotRow = Pick<DailyTotals, 'studio_internal_id' | 'metric_date' | 'total_clients_count'>

function getPartnerRankingName(row: Pick<PartnerRankingRow, 'partner_number' | 'persons_fio' | 'cities_text'>): string {
  const parts = [row.cities_text, row.persons_fio].filter((part): part is string => Boolean(part))
  return parts.length > 0 ? `${parts.join(', ')} (#${row.partner_number})` : `Франчайзи #${row.partner_number}`
}

function buildPartnerRangeName(row: Pick<PartnerRankingRow, 'partner_number' | 'persons_fio' | 'cities_text'>): string {
  if (row.persons_fio?.trim()) return `${row.persons_fio.trim()} (${row.partner_number})`
  if (row.cities_text?.trim()) return `${row.cities_text.trim()} (${row.partner_number})`
  return `Франчайзи ${row.partner_number}`
}

function toNumber(value: number | string | null | undefined): number {
  const normalized = typeof value === 'string' ? Number(value) : value
  return Number.isFinite(normalized) ? Number(normalized) : 0
}

function shiftIsoDate(dateString: string, daysShift: number): string {
  const date = new Date(`${dateString}T00:00:00`)
  date.setDate(date.getDate() + daysShift)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function pushClientSnapshotRow(
  map: Map<number, ClientMetricSnapshotRow[]>,
  row: ClientMetricSnapshotRow
) {
  const existing = map.get(row.studio_internal_id) || []
  existing.push(row)
  map.set(row.studio_internal_id, existing)
}

function buildDisplayEntries(
  entries: Array<{
    studio_id: number
    studio_number: string
    city_name?: string
    name?: string
    value: number
  }>,
  currentId: number,
  topCount: number = RANKING_TOP_COUNT,
  neighborRadius: number = RANKING_NEIGHBOR_RADIUS
) {
  const rankedEntries = rankEntries(entries)

  const currentPosition = rankedEntries.find((entry) => entry.studio_id === currentId)?.position ?? 1
  const topEntries = rankedEntries.filter((entry) => entry.position <= topCount)

  if (currentPosition <= topCount) {
    return {
      currentPosition,
      total: rankedEntries.length,
      entries: topEntries
    }
  }

  const neighborStart = Math.max(1, currentPosition - neighborRadius)
  const neighborEnd = currentPosition + neighborRadius
  const merged = new Map<string, typeof rankedEntries[number]>()

  for (const entry of topEntries) {
    merged.set(`${entry.position}-${entry.studio_id}`, entry)
  }
  for (const entry of rankedEntries.filter((row) => row.position >= neighborStart && row.position <= neighborEnd)) {
    merged.set(`${entry.position}-${entry.studio_id}`, entry)
  }

  return {
    currentPosition,
    total: rankedEntries.length,
    entries: Array.from(merged.values()).sort((a, b) => a.position - b.position)
  }
}

function rankEntries(
  entries: Array<{
    studio_id: number
    studio_number: string
    city_name?: string
    name?: string
    value: number
  }>,
  currentId?: number
) {
  return entries.reduce<Array<{
    position: number
    studio_id: number
    studio_number: string
    city_name?: string
    name?: string
    value: number
    isCurrentStudio: boolean
  }>>((acc, entry, index) => {
    const previous = entries[index - 1]
    const position = index === 0
      ? 1
      : previous && previous.value === entry.value
        ? acc[acc.length - 1].position
        : index + 1

    acc.push({
      position,
      studio_id: entry.studio_id,
      studio_number: entry.studio_number,
      city_name: entry.city_name,
      name: entry.name,
      value: entry.value,
      isCurrentStudio: entry.studio_id === currentId
    })

    return acc
  }, [])
}

function sortStudioRankingEntries<T extends {
  value: number
  studio_number: string
  city_name?: string
  name?: string
}>(entries: T[]): T[] {
  return entries.sort((a, b) => {
    if (b.value !== a.value) return b.value - a.value
    const nameA = a.name || a.city_name || ''
    const nameB = b.name || b.city_name || ''
    const labelCompare = nameA.localeCompare(nameB, 'ru', { numeric: true })
    return labelCompare !== 0
      ? labelCompare
      : a.studio_number.localeCompare(b.studio_number, 'ru', { numeric: true })
  })
}

async function fetchEligibleStudioIdSetAtDate(studioIds: number[], metricDate: string): Promise<Set<number>> {
  if (studioIds.length === 0) return new Set<number>()

  const { data, error } = await ((supabase as any)
    .from('studios')
    .select('studio_internal_id, status_code')
    .in('studio_internal_id', studioIds) as {
      data: Array<{ studio_internal_id: number; status_code: string | null }> | null
      error: any
    })

  if (error) {
    throw error
  }

  const currentStatusByStudioId = new Map(
    (data || []).map((row) => [Number(row.studio_internal_id), row.status_code ?? 'CLOSED'])
  )
  const effectiveStatusByStudioId = await loadEffectiveStudioStatusMap(
    supabase as never as Parameters<typeof loadEffectiveStudioStatusMap>[0],
    studioIds,
    metricDate,
    currentStatusByStudioId
  )

  return new Set(
    Array.from(effectiveStatusByStudioId.entries())
      .filter(([, statusCode]) => isRatingEligibleStudioStatus(statusCode))
      .map(([studioId]) => studioId)
  )
}

async function resolveRankingReferenceDateForMonth(month: string): Promise<string> {
  const [yearPart, monthPart] = month.split('-')
  const yearNumber = Number(yearPart)
  const monthNumber = Number(monthPart)
  if (!Number.isFinite(yearNumber) || !Number.isFinite(monthNumber)) return getLocalIsoDate()

  const { data, error } = await (supabase as any)
    .from('metric_periods')
    .select('period_start, period_end')
    .eq('year_number', yearNumber)
    .eq('month_number', monthNumber)
    .order('period_end', { ascending: false })
    .limit(1)
    .maybeSingle() as {
      data: { period_start: string; period_end: string } | null
      error: any
    }

  if (error || !data) return getLocalIsoDate()

  const today = getLocalIsoDate()
  return today < data.period_end ? today : data.period_end
}

async function fetchMonthlyOpenRankingRows(month: string): Promise<{ rows: RankingRow[]; referenceDate: string }> {
  const { data, error } = await (supabase as any)
    .from('studio_all_rankings')
    .select(`
      studio_internal_id,
      studio_number,
      city_name,
      country,
      land_group_name,
      city_population_group,
      population_group_description,
      month_year,
      total_revenue,
      global_rank,
      global_total,
      regional_rank,
      regional_total,
      region_name,
      size_group_rank,
      size_group_total,
      size_group_name
    `)
    .eq('month_year', month) as { data: RankingRow[] | null, error: any }

  if (error) {
    throw error
  }

  const rows = data || []
  const referenceDate = await resolveRankingReferenceDateForMonth(month)
  const openStudioIdSet = await fetchEligibleStudioIdSetAtDate(rows.map((row) => row.studio_internal_id), referenceDate)
  return {
    rows: rows.filter((row) => openStudioIdSet.has(row.studio_internal_id)),
    referenceDate,
  }
}

function buildStudioRevenueEntries(rows: RankingRow[]) {
  return sortStudioRankingEntries(rows.map((row) => ({
    studio_id: row.studio_internal_id,
    studio_number: String(row.studio_number),
    city_name: row.city_name || undefined,
    value: Number(row.total_revenue || 0),
    region_name: row.region_name || null,
    city_population_group: row.city_population_group ?? null,
    size_group_name: row.size_group_name || null,
  })))
}

async function fetchOwnerPartnerByStudioAtDate(
  studioIds: number[],
  metricDate: string
): Promise<Map<number, number | null>> {
  if (studioIds.length === 0) return new Map<number, number | null>()

  const { data, error } = await (supabase as any)
    .from('studio_ownerships')
    .select(`
      ownership_id,
      studio_internal_id,
      start_date,
      end_date,
      updated_at,
      contract_series!inner (
        partner_number
      )
    `)
    .in('studio_internal_id', studioIds) as {
      data: OwnershipRow[] | null
      error: any
    }

  if (error) {
    throw error
  }

  const ownershipsByStudioId = new Map<number, OwnershipRow[]>()
  for (const row of data || []) {
    const current = ownershipsByStudioId.get(row.studio_internal_id) || []
    current.push(row)
    ownershipsByStudioId.set(row.studio_internal_id, current)
  }

  const ownerByStudioId = new Map<number, number | null>()
  for (const studioId of studioIds) {
    const activeRows = (ownershipsByStudioId.get(studioId) || []).filter((row) => {
      const startDate = row.start_date ? String(row.start_date).slice(0, 10) : null
      const endDate = row.end_date ? String(row.end_date).slice(0, 10) : null
      return (!startDate || startDate <= metricDate) && (!endDate || endDate > metricDate)
    })

    activeRows.sort((left, right) => {
      const leftMoment = String(left.updated_at || left.start_date || '')
      const rightMoment = String(right.updated_at || right.start_date || '')
      if (leftMoment !== rightMoment) return rightMoment.localeCompare(leftMoment)
      return Number(right.ownership_id || 0) - Number(left.ownership_id || 0)
    })

    const winner = activeRows[0]
    const contractSeries = Array.isArray(winner?.contract_series)
      ? winner?.contract_series[0]
      : winner?.contract_series
    ownerByStudioId.set(studioId, contractSeries?.partner_number ?? null)
  }

  return ownerByStudioId
}

async function fetchRevenueRangeWithPartner(
  startDate: string,
  endDate: string
): Promise<RevenueRangeRow[]> {
  // Supabase RPC responses are capped to 1000 rows per call in practice, even if
  // the SQL function accepts a larger limit. Keep the client page size aligned so
  // we don't stop early and silently truncate the ranking source.
  const pageSize = 1000
  const allRows: RevenueRangeRow[] = []
  let offset = 0

  while (true) {
    const { data, error } = await (supabase as any).rpc('get_revenue_range_with_partner', {
      p_date_from: startDate,
      p_date_to: endDate,
      p_limit: pageSize,
      p_offset: offset
    }) as { data: RevenueRangeRow[] | null, error: any }

    if (error) {
      throw error
    }

    const rows = data || []
    if (rows.length === 0) break

    allRows.push(...rows)
    offset += rows.length
  }

  return allRows
}

async function fetchClientSnapshotsRange(
  studioIds: number[],
  startDate: string,
  endDate: string
): Promise<ClientMetricSnapshotRow[]> {
  if (studioIds.length === 0) return []

  const chunkSize = 150
  const pageSize = 1000
  const rows: ClientMetricSnapshotRow[] = []

  for (let index = 0; index < studioIds.length; index += chunkSize) {
    const chunk = studioIds.slice(index, index + chunkSize)
    let offset = 0

    while (true) {
      const { data, error } = await (supabase as any)
        .from('studio_daily_totals')
        .select('studio_internal_id, metric_date, total_clients_count')
        .in('studio_internal_id', chunk)
        .gte('metric_date', startDate)
        .lte('metric_date', endDate)
        .order('metric_date', { ascending: true })
        .order('studio_internal_id', { ascending: true })
        .range(offset, offset + pageSize - 1) as {
          data: ClientMetricSnapshotRow[] | null
          error: any
        }

      if (error) {
        throw error
      }

      const batch = data || []
      rows.push(...batch)

      if (batch.length < pageSize) break
      offset += batch.length
    }
  }

  return rows
}

async function loadPartnerRankingEntries(
  rows: RankingRow[],
  referenceDate: string,
  partnerNumber?: number | null
): Promise<{
  position: number
  total: number
  revenue: number
  name: string
  entries: Array<{
    position: number
    studio_id: number
    studio_number: string
    city_name?: string
    name?: string
    value: number
    isCurrentStudio: boolean
  }>
} | null> {
  if (partnerNumber == null || Number.isNaN(partnerNumber)) return null

  const [ownerByStudioId, partnerCoverage] = await Promise.all([
    fetchOwnerPartnerByStudioAtDate(rows.map((row) => row.studio_internal_id), referenceDate),
    listPartnerCoverage(),
  ])

  const coverageByPartnerNumber = new Map<number, PartnerRankingRow>(
    partnerCoverage.map((row) => [
      Number(row.partner_number),
      {
        partner_number: Number(row.partner_number),
        persons_fio: row.persons_fio,
        cities_text: row.cities_text,
        total_revenue: 0,
        partner_rank: 0,
        partner_total: 0,
      }
    ])
  )

  const revenueByPartnerNumber = new Map<number, number>()
  for (const row of rows) {
    const resolvedPartnerNumber = ownerByStudioId.get(row.studio_internal_id)
    if (resolvedPartnerNumber == null) continue
    revenueByPartnerNumber.set(
      resolvedPartnerNumber,
      (revenueByPartnerNumber.get(resolvedPartnerNumber) || 0) + Number(row.total_revenue || 0)
    )
  }

  if (!revenueByPartnerNumber.has(Number(partnerNumber))) return null

  const entries = sortStudioRankingEntries(
    Array.from(revenueByPartnerNumber.entries()).map(([resolvedPartnerNumber, totalRevenue]) => {
      const coverage = coverageByPartnerNumber.get(resolvedPartnerNumber)
      return {
        studio_id: resolvedPartnerNumber,
        studio_number: String(resolvedPartnerNumber),
        city_name: coverage?.cities_text || undefined,
        name: buildPartnerRangeName({
          partner_number: resolvedPartnerNumber,
          persons_fio: coverage?.persons_fio,
          cities_text: coverage?.cities_text,
        }),
        value: totalRevenue,
      }
    })
  )

  const ranking = buildDisplayEntries(entries, Number(partnerNumber))
  const currentEntry = entries.find((entry) => entry.studio_id === Number(partnerNumber))
  if (!currentEntry) return null

  return {
    position: ranking.currentPosition,
    total: ranking.total,
    revenue: currentEntry.value,
    name: currentEntry.name || getPartnerRankingName({
      partner_number: Number(partnerNumber),
      persons_fio: null,
      cities_text: null,
    }),
    entries: ranking.entries.map((entry) => ({
      position: entry.position,
      studio_id: entry.studio_id,
      studio_number: entry.studio_number,
      city_name: entry.city_name,
      name: entry.name,
      value: entry.value,
      isCurrentStudio: entry.studio_id === Number(partnerNumber),
    }))
  }
}

// Новая функция для загрузки рейтингов из view
export async function loadStudioRankingsFromView(
  studioId: number, 
  currentMonth?: string,
  partnerNumber?: number | null
): Promise<StudioRankingData | null> {
  try {
    const month = currentMonth || getLocalIsoDate().slice(0, 7) // YYYY-MM format
    const { rows: openRows, referenceDate } = await fetchMonthlyOpenRankingRows(month)
    const targetRow = openRows.find((row) => row.studio_internal_id === studioId)

    if (!targetRow) {
      console.warn('No ranking data found for open studio:', studioId)
      return null
    }

    const studioEntries = buildStudioRevenueEntries(openRows)
    const globalRanking = buildDisplayEntries(studioEntries, studioId)
    const currentStudioMetricValue = studioEntries.find((row) => row.studio_id === studioId)?.value || 0

    const regionalEntries = studioEntries.filter((row) => row.region_name === (targetRow.region_name || null))
    const regionalRanking = buildDisplayEntries(regionalEntries, studioId)

    const sizeGroupEntries = studioEntries.filter(
      (row) => row.city_population_group === (targetRow.city_population_group ?? null)
    )
    const sizeGroupRanking = buildDisplayEntries(sizeGroupEntries, studioId)

    const partnerRanking = await loadPartnerRankingEntries(openRows, referenceDate, partnerNumber)

    return {
      global_position: globalRanking.currentPosition,
      global_total: globalRanking.total,
      global_rankings: globalRanking.entries,
      partner_position: partnerRanking?.position,
      partner_total: partnerRanking?.total,
      partner_name: partnerRanking?.name,
      partner_revenue: partnerRanking?.revenue,
      partner_rankings: partnerRanking?.entries,
      regional_position: regionalRanking.currentPosition,
      regional_total: regionalRanking.total,
      regional_name: targetRow.region_name || 'Без земли',
      regional_rankings: regionalRanking.entries,
      size_group_position: sizeGroupRanking.currentPosition,
      size_group_total: sizeGroupRanking.total,
      size_group_name: targetRow.size_group_name || 'Без группы',
      size_group_rankings: sizeGroupRanking.entries,
      studio_revenue: currentStudioMetricValue
    }
  } catch (error) {
    console.error('Failed to load studio rankings from view:', error)
    return null
  }
}

export async function loadStudioRankingsForRange(params: {
  studioId: number
  period: { year_number: number; month_number: number }
  periodId?: number | null
  previousPeriodId?: number | null
  periodStart?: string
  rangeMode?: 'period' | 'week'
  startDate: string
  endDate: string
  partnerNumber?: number | null
}): Promise<StudioRankingData | null> {
  try {
    const month = `${params.period.year_number}-${String(params.period.month_number).padStart(2, '0')}`
    const clientWindowStart = params.periodStart
      ? shiftIsoDate(params.periodStart, -6)
      : params.startDate
    const [metadataResponse, revenueRows, partnerCoverage] = await Promise.all([
      (supabase as any)
        .from('studio_all_rankings')
        .select('studio_internal_id, studio_number, city_name, region_name, city_population_group, size_group_name')
        .eq('month_year', month)
        .order('studio_number', { ascending: true }) as Promise<{ data: RangeStudioMetaRow[] | null, error: any }>,
      fetchRevenueRangeWithPartner(params.startDate, params.endDate),
      listPartnerCoverage()
    ])

    const { data: metadataRows, error: metadataError } = metadataResponse

    if (metadataError) {
      console.error('Error loading ranking metadata:', metadataError)
      return null
    }

    const allMetadata = (metadataRows || []).map((row) => ({
      ...row,
      studio_number: String(row.studio_number)
    }))

    const { data: openStudios, error: openStudiosError } = await (supabase as any)
      .from('studios')
      .select('studio_internal_id, studio_number')
      .in('studio_internal_id', allMetadata.map((row) => row.studio_internal_id))
      .eq('status_code', 'OPEN') as {
        data: Array<{ studio_internal_id: number; studio_number: string | number }> | null
        error: any
      }

    if (openStudiosError) {
      console.error('Error loading open studios for ranking:', openStudiosError)
      return null
    }

    const openStudioIdSet = new Set((openStudios || []).map((row) => Number(row.studio_internal_id)))
    const openStudioIdByNumber = new Map(
      (openStudios || []).map((row) => [String(row.studio_number), Number(row.studio_internal_id)])
    )
    const metadata = allMetadata.filter((row) => openStudioIdSet.has(row.studio_internal_id))
    const targetStudio = metadata.find((row) => row.studio_internal_id === params.studioId)
    if (!targetStudio) {
      console.warn('Target studio was not found among open studios for ranking:', params.studioId)
      return null
    }

    const openStudioIds = Array.from(openStudioIdSet)
    const revenueByStudioId = new Map<number, number>()
    const revenueByPartnerNumber = new Map<number, number>()
    for (const row of revenueRows) {
      const studioNumber = String(row.studio_number)
      const studioId = openStudioIdByNumber.get(studioNumber)
      if (!studioId) continue
      const revenue = toNumber(row.gross_revenue)

      revenueByStudioId.set(studioId, (revenueByStudioId.get(studioId) || 0) + revenue)
      if (row.partner_number != null) {
        revenueByPartnerNumber.set(
          Number(row.partner_number),
          (revenueByPartnerNumber.get(Number(row.partner_number)) || 0) + revenue
        )
      }
    }

    const clientsByStudioId = new Map<number, number>()
    const clientMovementByStudioId = new Map<number, number>()
    let clientMovementDebug: StudioRankingData['client_movement_debug'] | undefined

    try {
      const clientMetricRows = await fetchStudioClientRangeMetrics(supabase as any, {
        targetStudioId: params.studioId,
        startDate: params.startDate,
        endDate: params.endDate,
        periodId: params.periodId,
        periodStart: params.periodStart,
        rangeMode: params.rangeMode
      })

      for (const row of clientMetricRows) {
        clientsByStudioId.set(row.studio_internal_id, row.clients_at_end)
        clientMovementByStudioId.set(row.studio_internal_id, row.clients_growth)
      }

      const currentStudioMetrics = clientMetricRows.find((row) => row.studio_internal_id === params.studioId)
      if (currentStudioMetrics && targetStudio) {
        clientMovementDebug = {
          studio_id: targetStudio.studio_internal_id,
          studio_number: targetStudio.studio_number,
          range_start: params.startDate,
          range_end: params.endDate,
          range_mode: params.rangeMode,
          before_rows_count: 0,
          in_range_rows_count: 0,
          before_rows_tail: [],
          in_range_rows_tail: [],
          clients_at_start: currentStudioMetrics.clients_at_start,
          clients_at_end: currentStudioMetrics.clients_at_end,
          movement: currentStudioMetrics.clients_growth
        }
      }
    } catch (error) {
      console.warn('Falling back to daily snapshot studio client ranking calculation:', error)

      const todayIso = getLocalIsoDate()
      const clientRows = await fetchClientSnapshotsRange(openStudioIds, clientWindowStart, params.endDate)
      const clientRowsInRangeByStudioId = new Map<number, ClientMetricSnapshotRow[]>()
      const clientRowsBeforeRangeByStudioId = new Map<number, ClientMetricSnapshotRow[]>()

      for (const row of clientRows) {
        if (!openStudioIdSet.has(row.studio_internal_id) || row.total_clients_count == null) continue
        const clients = toNumber(row.total_clients_count)
        const snapshotRow: ClientMetricSnapshotRow = {
          studio_internal_id: row.studio_internal_id,
          metric_date: row.metric_date,
          total_clients_count: clients
        }

        if (row.metric_date < params.startDate) {
          pushClientSnapshotRow(clientRowsBeforeRangeByStudioId, snapshotRow)
          continue
        }

        pushClientSnapshotRow(clientRowsInRangeByStudioId, snapshotRow)
      }

      for (const studioMeta of metadata) {
        const studioId = studioMeta.studio_internal_id
        const dataInRange = (clientRowsInRangeByStudioId.get(studioId) || []) as DailyTotals[]
        const dataBeforeRange = (clientRowsBeforeRangeByStudioId.get(studioId) || []) as DailyTotals[]
        const clientsAtStart = resolveRangeGrowthStartMetricValue({
          dataInRange,
          dataBeforeRange,
          key: 'total_clients_count',
          rangeStart: params.startDate,
          rangeEnd: params.endDate,
          allowGrowthFallback: false
        })
        const clientsAtEnd = resolveRangeEndMetricValue({
          dataInRange,
          dataBeforeRange,
          key: 'total_clients_count',
          rangeStart: params.startDate,
          rangeEnd: params.endDate,
          todayIso
        })

        if (clientsAtEnd !== undefined) {
          clientsByStudioId.set(studioId, clientsAtEnd)
        }

        const movement = clientsAtStart !== undefined && clientsAtEnd !== undefined
          ? clientsAtEnd - clientsAtStart
          : 0
        clientMovementByStudioId.set(studioId, movement)

        if (studioId === params.studioId) {
          clientMovementDebug = {
            studio_id: studioMeta.studio_internal_id,
            studio_number: studioMeta.studio_number,
            range_start: params.startDate,
            range_end: params.endDate,
            range_mode: params.rangeMode,
            before_rows_count: dataBeforeRange.length,
            in_range_rows_count: dataInRange.length,
            before_rows_tail: dataBeforeRange
              .slice()
              .sort((a, b) => a.metric_date.localeCompare(b.metric_date))
              .slice(-10)
              .map((row) => ({
                metric_date: row.metric_date,
                total_clients_count: row.total_clients_count ?? null
              })),
            in_range_rows_tail: dataInRange
              .slice()
              .sort((a, b) => a.metric_date.localeCompare(b.metric_date))
              .slice(-15)
              .map((row) => ({
                metric_date: row.metric_date,
                total_clients_count: row.total_clients_count ?? null
              })),
            clients_at_start: clientsAtStart,
            clients_at_end: clientsAtEnd,
            movement
          }
        }
      }
    }

    const studioEntries = metadata
      .map((row) => ({
        studio_id: row.studio_internal_id,
        studio_number: row.studio_number,
        city_name: row.city_name || undefined,
        value: revenueByStudioId.get(row.studio_internal_id) || 0,
        region_name: row.region_name || null,
        city_population_group: row.city_population_group ?? null,
        size_group_name: row.size_group_name || null
      }))
      .sort((a, b) => {
        if (b.value !== a.value) return b.value - a.value
        const cityCompare = (a.city_name || '').localeCompare(b.city_name || '', 'ru', { numeric: true })
        return cityCompare !== 0
          ? cityCompare
          : a.studio_number.localeCompare(b.studio_number, 'ru', { numeric: true })
      })

    const globalRanking = buildDisplayEntries(studioEntries, params.studioId)
    const currentStudioMetricValue = studioEntries.find((row) => row.studio_id === params.studioId)?.value || 0

    const regionalEntries = studioEntries.filter((row) => row.region_name === (targetStudio.region_name || null))
    const regionalRanking = buildDisplayEntries(regionalEntries, params.studioId)

    const sizeGroupEntries = studioEntries.filter(
      (row) => row.city_population_group === (targetStudio.city_population_group ?? null)
    )
    const sizeGroupRanking = buildDisplayEntries(sizeGroupEntries, params.studioId)

    const clientStudioEntries = metadata
      .map((row) => ({
        studio_id: row.studio_internal_id,
        studio_number: row.studio_number,
        city_name: row.city_name || undefined,
        value: clientsByStudioId.get(row.studio_internal_id) || 0
      }))
      .sort((a, b) => {
        if (b.value !== a.value) return b.value - a.value
        const cityCompare = (a.city_name || '').localeCompare(b.city_name || '', 'ru', { numeric: true })
        return cityCompare !== 0
          ? cityCompare
          : a.studio_number.localeCompare(b.studio_number, 'ru', { numeric: true })
      })
    const clientGlobalRanking = buildDisplayEntries(clientStudioEntries, params.studioId)
    const currentStudioClientsValue = clientStudioEntries.find((row) => row.studio_id === params.studioId)?.value || 0

    const clientMovementEntries = metadata
      .map((row) => ({
        studio_id: row.studio_internal_id,
        studio_number: row.studio_number,
        city_name: row.city_name || undefined,
        value: clientMovementByStudioId.get(row.studio_internal_id) || 0
      }))
      .sort((a, b) => {
        if (b.value !== a.value) return b.value - a.value
        const cityCompare = (a.city_name || '').localeCompare(b.city_name || '', 'ru', { numeric: true })
        return cityCompare !== 0
          ? cityCompare
          : a.studio_number.localeCompare(b.studio_number, 'ru', { numeric: true })
      })
    const clientMovementRanking = buildDisplayEntries(clientMovementEntries, params.studioId)
    const currentStudioClientMovementValue = clientMovementEntries.find((row) => row.studio_id === params.studioId)?.value || 0

    const partnerCoverageByNumber = new Map<number, PartnerRankingRow>(
      partnerCoverage.map((row) => [
        Number(row.partner_number),
        {
          partner_number: Number(row.partner_number),
          persons_fio: row.persons_fio,
          cities_text: row.cities_text,
          total_revenue: 0,
          partner_rank: 0,
          partner_total: 0
        }
      ])
    )

    if (params.partnerNumber != null && !partnerCoverageByNumber.has(Number(params.partnerNumber))) {
      partnerCoverageByNumber.set(Number(params.partnerNumber), {
        partner_number: Number(params.partnerNumber),
        persons_fio: null,
        cities_text: null,
        total_revenue: 0,
        partner_rank: 0,
        partner_total: 0
      })
    }

    const partnerNumbers = new Set<number>([
      ...Array.from(partnerCoverageByNumber.keys()),
      ...Array.from(revenueByPartnerNumber.keys())
    ])

    const partnerEntries = Array.from(partnerNumbers)
      .map((partnerNumber) => {
        const coverage = partnerCoverageByNumber.get(Number(partnerNumber))
        return {
          studio_id: Number(partnerNumber),
          studio_number: String(partnerNumber),
          name: buildPartnerRangeName({
            partner_number: Number(partnerNumber),
            persons_fio: coverage?.persons_fio,
            cities_text: coverage?.cities_text
          }),
          city_name: coverage?.cities_text || undefined,
          value: revenueByPartnerNumber.get(Number(partnerNumber)) || 0
        }
      })
      .sort((a, b) => {
        if (b.value !== a.value) return b.value - a.value
        return (a.name || '').localeCompare(b.name || '', 'ru', { numeric: true })
      })

    const partnerRanking = params.partnerNumber != null
      ? buildDisplayEntries(partnerEntries, Number(params.partnerNumber))
      : null
    const currentPartnerEntry = params.partnerNumber != null
      ? partnerEntries.find((entry) => entry.studio_id === Number(params.partnerNumber))
      : null

    return {
      metric_kind: 'revenue',
      metric_label: 'Выручка',
      global_position: globalRanking.currentPosition,
      global_total: globalRanking.total,
      global_rankings: globalRanking.entries,
      partner_position: partnerRanking?.currentPosition,
      partner_total: partnerRanking?.total,
      partner_name: currentPartnerEntry?.name,
      partner_metric_value: currentPartnerEntry?.value,
      partner_revenue: currentPartnerEntry?.value,
      partner_rankings: partnerRanking?.entries,
      regional_position: regionalRanking.currentPosition,
      regional_total: regionalRanking.total,
      regional_name: targetStudio.region_name || 'Без земли',
      regional_rankings: regionalRanking.entries,
      size_group_position: sizeGroupRanking.currentPosition,
      size_group_total: sizeGroupRanking.total,
      size_group_name: targetStudio.size_group_name || 'Без группы',
      size_group_rankings: sizeGroupRanking.entries,
      studio_metric_value: currentStudioMetricValue,
      studio_revenue: currentStudioMetricValue,
      client_global_position: clientGlobalRanking.currentPosition,
      client_global_total: clientGlobalRanking.total,
      client_global_rankings: clientGlobalRanking.entries,
      studio_clients_value: currentStudioClientsValue,
      client_movement_position: clientMovementRanking.currentPosition,
      client_movement_total: clientMovementRanking.total,
      client_movement_rankings: clientMovementRanking.entries,
      studio_client_movement_value: currentStudioClientMovementValue,
      client_movement_debug: clientMovementDebug
    }
  } catch (error) {
    console.error('Failed to load studio rankings for range:', error)
    return null
  }
}

// Функция для получения топ-рейтингов (например, для дашборда)
export async function loadTopRankings(
  month?: string,
  rankingType: 'global' | 'regional' | 'size_group' = 'global',
  limit: number = 10
) {
  try {
    const targetMonth = month || getLocalIsoDate().slice(0, 7)
    const { rows: openRows } = await fetchMonthlyOpenRankingRows(targetMonth)

    const studioEntries = buildStudioRevenueEntries(openRows)
    const rankField = rankingType === 'global'
      ? 'global_rank'
      : rankingType === 'regional'
        ? 'regional_rank'
        : 'size_group_rank'

    const totalsByRegion = new Map<string | null, number>()
    const totalsBySizeGroup = new Map<number | null, number>()
    for (const row of openRows) {
      totalsByRegion.set(row.region_name || null, (totalsByRegion.get(row.region_name || null) || 0) + 1)
      totalsBySizeGroup.set(row.city_population_group ?? null, (totalsBySizeGroup.get(row.city_population_group ?? null) || 0) + 1)
    }

    const globalRanks = new Map(rankEntries(studioEntries).map((entry) => [entry.studio_id, entry.position]))
    const regionalRanks = new Map<number, number>()
    const sizeGroupRanks = new Map<number, number>()

    const regionalGroups = new Map<string | null, typeof studioEntries>()
    const sizeGroups = new Map<number | null, typeof studioEntries>()
    for (const entry of studioEntries) {
      const regionKey = entry.region_name || null
      const sizeKey = entry.city_population_group ?? null
      ;(regionalGroups.get(regionKey) || regionalGroups.set(regionKey, []).get(regionKey)!).push(entry)
      ;(sizeGroups.get(sizeKey) || sizeGroups.set(sizeKey, []).get(sizeKey)!).push(entry)
    }

    for (const entries of regionalGroups.values()) {
      for (const rankedEntry of rankEntries(entries)) {
        regionalRanks.set(rankedEntry.studio_id, rankedEntry.position)
      }
    }
    for (const entries of sizeGroups.values()) {
      for (const rankedEntry of rankEntries(entries)) {
        sizeGroupRanks.set(rankedEntry.studio_id, rankedEntry.position)
      }
    }

    return openRows
      .map((row) => ({
        studio_id: row.studio_internal_id,
        studio_number: String(row.studio_number),
        city_name: row.city_name || '',
        country: row.country || '',
        land_group_name: row.land_group_name || null,
        city_population_group: row.city_population_group ?? 0,
        population_group_description: row.population_group_description || null,
        month_year: row.month_year || targetMonth,
        total_revenue: Number(row.total_revenue || 0),
        global_rank: globalRanks.get(row.studio_internal_id) || 0,
        global_total: studioEntries.length,
        regional_rank: regionalRanks.get(row.studio_internal_id) || 0,
        regional_total: totalsByRegion.get(row.region_name || null) || 0,
        region_name: row.region_name || 'Без земли',
        size_group_rank: sizeGroupRanks.get(row.studio_internal_id) || 0,
        size_group_total: totalsBySizeGroup.get(row.city_population_group ?? null) || 0,
        size_group_name: row.size_group_name || 'Без группы',
      }))
      .sort((a, b) => {
        const rankA = rankingType === 'global' ? a.global_rank : rankingType === 'regional' ? a.regional_rank : a.size_group_rank
        const rankB = rankingType === 'global' ? b.global_rank : rankingType === 'regional' ? b.regional_rank : b.size_group_rank
        if (rankA !== rankB) return rankA - rankB
        if (b.total_revenue !== a.total_revenue) return b.total_revenue - a.total_revenue
        return a.studio_number.localeCompare(b.studio_number, 'ru', { numeric: true })
      })
      .slice(0, limit)
  } catch (error) {
    console.error('Failed to load top rankings:', error)
    return []
  }
}

// Простая функция для поиска позиции любой студии
export async function getStudioPosition(
  studioId: number,
  month?: string
): Promise<{ global: number; regional: number; sizeGroup: number } | null> {
  try {
    const targetMonth = month || getLocalIsoDate().slice(0, 7)

    const { rows: openRows } = await fetchMonthlyOpenRankingRows(targetMonth)
    const targetRow = openRows.find((row) => row.studio_internal_id === studioId)
    if (!targetRow) {
      return null
    }

    const studioEntries = buildStudioRevenueEntries(openRows)
    const globalPosition = rankEntries(studioEntries).find((entry) => entry.studio_id === studioId)?.position ?? 0
    const regionalPosition = rankEntries(
      studioEntries.filter((row) => row.region_name === (targetRow.region_name || null))
    ).find((entry) => entry.studio_id === studioId)?.position ?? 0
    const sizeGroupPosition = rankEntries(
      studioEntries.filter((row) => row.city_population_group === (targetRow.city_population_group ?? null))
    ).find((entry) => entry.studio_id === studioId)?.position ?? 0

    return {
      global: globalPosition,
      regional: regionalPosition,
      sizeGroup: sizeGroupPosition
    }
  } catch (error) {
    console.error('Failed to get studio position:', error)
    return null
  }
}

// Оставляем старую функцию для совместимости
export async function loadStudioRankings(
  studioId: number, 
  currentMonth?: string,
  partnerNumber?: number | null
): Promise<StudioRankingData | null> {
  return loadStudioRankingsFromView(studioId, currentMonth, partnerNumber)
}

// Функция для форматирования денежных значений
export function formatRankingValue(value: number, metric: string): string {
  if (metric.includes('выручка') || metric.includes('₽')) {
    const absValue = Math.abs(value)
    const sign = value < 0 ? '-' : ''
    
    if (absValue >= 1000000) {
      return `${sign}${(absValue / 1000000).toFixed(1)}М`
    } else if (absValue >= 1000) {
      return `${sign}${Math.round(absValue / 1000)}к`
    } else {
      return `${sign}${Math.round(absValue)}`
    }
  } else if (metric.includes('посетител')) {
    return `${Math.round(value)}`
  } else {
    return `${Math.round(value)}`
  }
}
